<template>
  <AwModal v-model:open="open" size="lg" position="top" is-dismissible>
    <template #body>
      <div :class="ui.card">
        <AwTextField
          v-model="name"
          :placeholder="$t('tasks.create.placeholder')"
          autofocus
          is-borderless
          :class="ui.input"
          @keydown.enter="submit"
        />

        <div :class="ui.toolbar">
          <div :class="ui.toolbarLeft">
            <AwSelectMenu
              :model-value="selectedActionType ?? undefined"
              :items="actionItems"
              value-key="value"
              size="sm"
              :search-input="{ placeholder: $t('tasks.create.actionSearch') }"
              :filter-fields="['label']"
              content-class="w-72"
              :class="ui.pillSelect"
              @update:model-value="(value) => (selectedActionType = value)"
            >
              <template #default>
                <span :class="ui.pillLabel">
                  <AwIcon :name="selectedActionDetail?.icon ?? 'i-ph-lightning'" />
                  {{ selectedActionDetail?.label ?? $t("tasks.create.action") }}
                </span>
              </template>
            </AwSelectMenu>

            <AwTooltip :text="sourceTooltip">
              <span class="inline-flex h-7! gap-1.5">
                <template v-if="subjects.length > 0">
                  <AwBadge
                    v-for="subject in subjects"
                    :key="`${subject.entity}-${subject.entity_id}`"
                    :label="$t(`tasks.subject.${subject.entity}`)"
                    leading-icon="i-ph-link"
                    variant="outline"
                    color="secondary"
                    size="sm"
                    is-removable
                    class="h-7! items-center rounded-lg"
                    @remove="unlinkSubject(subject)"
                  />
                </template>
                <AwButton
                  v-else
                  variant="outline"
                  color="secondary"
                  leading-icon="i-ph-link"
                  :label="$t('tasks.create.source')"
                  disabled
                />
              </span>
            </AwTooltip>

            <TeamMemberSelectV2
              v-model:value="assignedToId"
              :multiple="false"
              :title="$t('tasks.create.assignedTo')"
              :class="ui.pillSelect"
            />

            <div :class="ui.datepicker">
              <Datepicker :date-value="dueDate" @update:date-value="onDueDateChange">
                <template #trigger>
                  <AwButton
                    class="h-7!"
                    variant="outline"
                    color="secondary"
                    leading-icon="i-ph-calendar-blank"
                    :label="dueDateLabel"
                  />
                </template>
              </Datepicker>
            </div>
          </div>

          <AwButton
            size="sm"
            :label="$t('common.actions.create')"
            class="shrink-0"
            :disabled="!canSubmit"
            :is-loading="isSubmitting"
            @click="submit"
          />
        </div>
      </div>
    </template>
  </AwModal>
</template>

<script setup lang="ts">
import { AwBadge, AwButton, AwIcon, AwModal, AwSelectMenu, AwTextField, AwTooltip } from "@asap/design";
import type { TaskAction } from "@asap/shared/schemas/task/action-type";
import type { TaskCreateBody } from "@asap/shared/schemas/task/crud";
import type { TaskSubject } from "@asap/shared/schemas/task/context";
import { z } from "zod";
import Datepicker, { type DatepickerValue } from "~/components/shared/date/Datepicker.vue";
import TeamMemberSelectV2 from "~/components/team-member/TeamMemberSelectV2.vue";
import { formatTaskDueDateLabel, getTodayDate, toTaskDueDateOnly } from "~/components/task/task-date";
import { getTaskActionLabels, TASK_ACTION_CATALOG, TASK_ACTIONS } from "~/utils/task-action";
import { getTaskSubjectFromRoute } from "~/components/task/task-source";
import { useTaskTrigger } from "~/components/task/composables/useTaskTrigger";
import { useTasks } from "~/components/task/composables/useTasks";
import { useUserMe } from "~/components/user/composables/useUser";

const props = withDefaults(
  defineProps<{
    actionType?: TaskAction | null;
  }>(),
  {
    actionType: null,
  }
);

const open = defineModel<boolean>("open", { required: true });

const route = useRoute();
const { t } = useI18n();
const { teamMember } = useUserMe();
const { createTask } = useTasks();
const { notifyTaskCreated } = useTaskTrigger();

const name = ref("");
const selectedActionType = ref<TaskAction | null>(null);
const assignedToId = ref<string | undefined>(undefined);
const dueDate = ref(getTodayDate());
const subjects = ref<TaskSubject[]>([]);
const creationPath = ref("");
const isSubmitting = ref(false);

const taskActionLabels = getTaskActionLabels(t);

const actionItems = computed(() =>
  TASK_ACTIONS.map((action) => ({
    value: action,
    label: taskActionLabels[action],
    icon: TASK_ACTION_CATALOG[action].icon,
  }))
);

const selectedActionDetail = computed(() => {
  if (!selectedActionType.value) {
    return null;
  }

  return {
    ...TASK_ACTION_CATALOG[selectedActionType.value],
    label: taskActionLabels[selectedActionType.value],
  };
});

const sourceTooltip = computed(() => {
  if (subjects.value.length > 0) {
    return t("tasks.create.sourceFromPage");
  }
  return t("tasks.create.sourceUnavailable");
});

const dueDateLabel = computed(() => formatTaskDueDateLabel(dueDate.value, t));

const canSubmit = computed(() => name.value.trim().length > 0 && !isSubmitting.value);

const resetForm = () => {
  name.value = "";
  selectedActionType.value = props.actionType ?? null;
  assignedToId.value = teamMember.value?.id;
  dueDate.value = getTodayDate();
  const subjectFromRoute = getTaskSubjectFromRoute(route);
  subjects.value = subjectFromRoute ? [subjectFromRoute] : [];
  creationPath.value = route.path;
  isSubmitting.value = false;
};

const onDueDateChange = (value: DatepickerValue) => {
  if (value === undefined) {
    return;
  }
  dueDate.value = toTaskDueDateOnly(value);
};

const unlinkSubject = (subjectToRemove: TaskSubject) => {
  subjects.value = subjects.value.filter(
    (subject) => !(subject.entity === subjectToRemove.entity && subject.entity_id === subjectToRemove.entity_id)
  );
};

const submit = async () => {
  const trimmedName = name.value.trim();
  if (trimmedName.length === 0 || isSubmitting.value) {
    return;
  }

  isSubmitting.value = true;
  const currentTeamMemberId = teamMember.value?.id;
  const assignedToParse = z.string().uuid().safeParse(assignedToId.value);
  const assignedTo =
    assignedToParse.success && assignedToParse.data !== currentTeamMemberId ? assignedToParse.data : undefined;

  const hasContext = subjects.value.length > 0;

  const body: TaskCreateBody = {
    name: trimmedName,
    action_type: selectedActionType.value,
    due_date: dueDate.value,
    context: hasContext
      ? {
          subjects: subjects.value,
          // Always exists
          creation_path: creationPath.value,
        }
      : undefined,
  };

  try {
    const createdTask = await createTask(assignedTo ? { ...body, assigned_to: assignedTo } : body);
    open.value = false;
    notifyTaskCreated(createdTask);
  } catch {
    // Toast is already shown by createTask
  } finally {
    isSubmitting.value = false;
  }
};

watch(open, (isOpen) => {
  if (isOpen) {
    resetForm();
  }
});

const ui = {
  card: "flex flex-col p-4",
  input: "w-full",
  toolbar: "flex items-start justify-between gap-2 max-h-8!",
  toolbarLeft: "flex flex-nowrap items-center gap-1.5 min-w-0 flex-1",
  pillSelect: "min-w-0 h-7! rounded-lg!",
  pillLabel: "inline-flex items-center gap-1 text-sm rounded-lg!",
  datepicker: "w-auto shrink-0 h-7!",
};
</script>
